import { useState } from 'react'
import { Modal } from './Modal'
import { Thumb } from './Thumb'
import { dayFill } from './PlanView'
import { DAY_NAMES, type Category, type Item, type Lunch, type Week } from '@shared/types'

interface Props {
  week: Week
  source: Lunch
  categories: Category[]
  itemsById: Map<string, Item>
  busy: boolean
  onApply: (days: number[]) => void
  onClose: () => void
}

/**
 * Copy one day's lunch onto other days -- empty slots only, so nothing the
 * other parent already chose is replaced. The momentum move after the first
 * finished day: five foods once, then the week is mostly done.
 */
export function RepeatDay({ week, source, categories, itemsById, busy, onApply, onClose }: Props) {
  const sourceName = DAY_NAMES[source.dayOfWeek - 1]!
  const targets = week.lunches
    .filter((l) => l.dayOfWeek !== source.dayOfWeek && l.status !== 'skip')
    .map((l) => {
      const fill = dayFill(l, categories)
      // Which of the source's foods would land: only those whose slot is empty here.
      const landing = source.items.filter((i) => fill.empty.some((c) => c.id === i.categoryId))
      return { lunch: l, fill, landing }
    })
  const [chosen, setChosen] = useState<Set<number>>(() => new Set(targets.filter((t) => t.landing.length > 0).map((t) => t.lunch.dayOfWeek)))

  const foods = source.items.map((i) => itemsById.get(i.itemId)).filter((i): i is Item => Boolean(i))
  const count = chosen.size

  return (
    <Modal
      title={`Repeat ${sourceName}’s lunch`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <div className="spacer" />
          <button className="btn primary" disabled={busy || count === 0} onClick={() => onApply([...chosen].sort())}>
            {busy ? 'Filling…' : `Fill ${count} day${count === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <div className="repeat-foods">
        {foods.map((f) => (
          <span key={f.id} className="repeat-food">
            <Thumb emoji={f.emoji} imageKey={f.imageKey} name={f.name} />
            {f.name}
          </span>
        ))}
      </div>
      <p className="muted" style={{ fontSize: 13, margin: '10px 0 6px' }}>
        Only empty slots are filled. Anything already chosen on a day stays.
      </p>
      <div className="picker-list">
        {targets.map(({ lunch, fill, landing }) => {
          const name = DAY_NAMES[lunch.dayOfWeek - 1]!
          const on = chosen.has(lunch.dayOfWeek)
          return (
            <label key={lunch.id} className={`picker-option repeat-day ${landing.length === 0 ? 'nothing' : ''}`}>
              <input
                type="checkbox"
                checked={on}
                disabled={landing.length === 0}
                onChange={(e) =>
                  setChosen((prev) => {
                    const next = new Set(prev)
                    if (e.target.checked) next.add(lunch.dayOfWeek)
                    else next.delete(lunch.dayOfWeek)
                    return next
                  })
                }
              />
              <span className="name">
                {name}
                <span className="note muted">
                  {landing.length === 0
                    ? fill.empty.length === 0
                      ? 'already full'
                      : 'nothing to add here'
                    : `${landing.length} of ${fill.empty.length} empty slot${fill.empty.length === 1 ? '' : 's'} filled`}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </Modal>
  )
}
